import React, { useState, useEffect, useMemo } from 'react';
import { Images, Calendar, User, X, ChevronLeft, ChevronRight, Upload, Phone } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const absUrl = (u) => (u && u.startsWith('/') ? `${BACKEND}${u}` : u);
const fmtD = (d, pt) => d ? new Date(d + 'T00:00:00').toLocaleDateString(pt ? 'pt-PT' : 'en-IE', { weekday: 'short', day: '2-digit', month: 'long', year: 'numeric' }) : '-';

const AdminServicePhotos = ({ token, language, onPublish }) => {
  const pt = language === 'pt';
  const [data, setData] = useState(null);
  const [client, setClient] = useState('all');
  const [month, setMonth] = useState('all');
  const [viewer, setViewer] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/service-photos`, { headers: { Authorization: `Bearer ${token}` } }).then(r => setData(r.data)).catch(() => setData({ services: [], clients: [], total_photos: 0 }));
  }, [token]);

  const months = useMemo(() => [...new Set((data?.services || []).map(s => (s.date || '').slice(0, 7)).filter(Boolean))].sort().reverse(), [data]);
  const list = useMemo(() => (data?.services || []).filter(s => (client === 'all' || s.client_key === client) && (month === 'all' || (s.date || '').startsWith(month))), [data, client, month]);
  const byDate = useMemo(() => list.reduce((acc, s) => { (acc[s.date] = acc[s.date] || []).push(s); return acc; }, {}), [list]);
  const monthLabel = (m) => new Date(m + '-01T00:00:00').toLocaleDateString(pt ? 'pt-PT' : 'en-IE', { month: 'long', year: 'numeric' });

  if (!data) return <p className="text-sm text-gray-500">{pt ? 'A carregar fotos…' : 'Loading photos…'}</p>;

  const openViewer = (photos, idx) => setViewer({ photos, idx });
  const step = (d) => setViewer(v => ({ ...v, idx: (v.idx + d + v.photos.length) % v.photos.length }));

  return (
    <div className="bg-white rounded-xl shadow-sm border p-4" data-testid="service-photos">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="font-heading font-semibold text-gray-900 flex items-center gap-2"><Images className="text-[#1a5c3a]" size={18} /> {pt ? 'Fotos de Serviços (staff)' : 'Service photos (staff)'}</h3>
          <p className="text-xs text-gray-500">{data.total_photos} {pt ? 'fotos em' : 'photos across'} {data.services.length} {pt ? 'serviços · enviadas pela equipa, organizadas por data e cliente' : 'jobs · uploaded by staff, organised by date and client'}</p>
        </div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <select value={client} onChange={e => setClient(e.target.value)} data-testid="service-photos-client" className="border rounded-lg px-3 py-1.5 text-sm flex-1 sm:flex-none">
            <option value="all">{pt ? 'Todos os clientes' : 'All clients'} ({data.clients.length})</option>
            {data.clients.map(c => <option key={c.key} value={c.key}>{c.name} · {c.photos} {pt ? 'fotos' : 'photos'}</option>)}
          </select>
          <select value={month} onChange={e => setMonth(e.target.value)} data-testid="service-photos-month" className="border rounded-lg px-3 py-1.5 text-sm flex-1 sm:flex-none">
            <option value="all">{pt ? 'Todas as datas' : 'All dates'}</option>
            {months.map(m => <option key={m} value={m}>{monthLabel(m)}</option>)}
          </select>
        </div>
      </div>

      {list.length === 0 ? (
        <p className="text-sm text-gray-500 bg-gray-50 border border-dashed rounded-xl p-6 text-center" data-testid="service-photos-empty">{pt ? 'Ainda não há fotos enviadas pela equipa. As fotos tiradas pelo staff no detalhe do trabalho aparecem aqui automaticamente.' : 'No staff photos yet. Photos taken by staff in the job details appear here automatically.'}</p>
      ) : (
        <div className="space-y-6">
          {Object.keys(byDate).sort().reverse().map(date => (
            <div key={date} data-testid={`service-photos-date-${date}`}>
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-800 mb-2 capitalize"><Calendar size={14} className="text-[#1a5c3a]" /> {fmtD(date, pt)}</div>
              <div className="space-y-3">
                {byDate[date].map(s => {
                  const before = s.photos.filter(p => p.phase === 'before');
                  const after = s.photos.filter(p => p.phase !== 'before');
                  return (
                    <div key={s.id} className="border rounded-xl p-3" data-testid={`service-photos-job-${s.id}`}>
                      <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 flex items-center gap-2 flex-wrap"><User size={14} className="text-gray-400" /> {s.client_name}
                            {s.airbnb && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 font-semibold">AIRBNB</span>}
                            <span className="text-xs text-gray-500 font-normal">{s.service_type}{s.time ? ` · ${s.time}` : ''}</span></p>
                          <p className="text-xs text-gray-500 flex items-center gap-2 flex-wrap">{s.client_phone && <span className="inline-flex items-center gap-1"><Phone size={10} /> {s.client_phone}</span>}{s.address && <span>· {s.address}</span>}{s.staff?.length > 0 && <span>· {pt ? 'Staff' : 'Staff'}: {s.staff.join(', ')}</span>}</p>
                        </div>
                        {onPublish && before[0] && after[0] && (
                          <button onClick={() => onPublish({ title: `${s.service_type} — ${s.client_name.split(' ')[0]}`, service_type: s.service_type, before_image: absUrl(before[0].url), after_image: absUrl(after[0].url) })} data-testid="service-photos-publish"
                            className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-[#1a5c3a] text-[#1a5c3a] hover:bg-[#1a5c3a]/5"><Upload size={12} /> {pt ? 'Publicar no site' : 'Publish to site'}</button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {[['before', pt ? 'Antes' : 'Before', before], ['after', pt ? 'Depois' : 'After', after]].map(([k, label, arr]) => arr.length > 0 && (
                          <div key={k}>
                            <p className={`text-[11px] font-semibold mb-1 ${k === 'before' ? 'text-amber-700' : 'text-green-700'}`}>{label} ({arr.length})</p>
                            <div className="grid grid-cols-4 gap-1.5">
                              {arr.map((p, i) => (
                                <button key={p.url} type="button" onClick={() => openViewer(s.photos, s.photos.indexOf(p))} className="aspect-square rounded-lg overflow-hidden border hover:opacity-90" data-testid="service-photo-thumb">
                                  <img src={absUrl(p.url)} alt="" className="w-full h-full object-cover" loading="lazy" />
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {viewer && (
        <div className="fixed inset-0 bg-black/90 z-[70] flex items-center justify-center" onClick={() => setViewer(null)} data-testid="service-photo-viewer">
          <button className="absolute top-4 right-4 text-white bg-black/50 rounded-full p-2" onClick={() => setViewer(null)}><X size={20} /></button>
          <button className="absolute left-2 sm:left-6 text-white bg-black/50 rounded-full p-2" onClick={(e) => { e.stopPropagation(); step(-1); }}><ChevronLeft size={22} /></button>
          <img src={absUrl(viewer.photos[viewer.idx].url)} alt="" className="max-w-[92vw] max-h-[85vh] rounded-lg" onClick={e => e.stopPropagation()} />
          <button className="absolute right-2 sm:right-6 text-white bg-black/50 rounded-full p-2" onClick={(e) => { e.stopPropagation(); step(1); }}><ChevronRight size={22} /></button>
          <p className="absolute bottom-4 text-white text-xs bg-black/50 px-3 py-1 rounded-full">{viewer.photos[viewer.idx].phase === 'before' ? (pt ? 'Antes' : 'Before') : (pt ? 'Depois' : 'After')} · {viewer.idx + 1}/{viewer.photos.length}{viewer.photos[viewer.idx].staff_name ? ` · ${viewer.photos[viewer.idx].staff_name}` : ''}</p>
        </div>
      )}
    </div>
  );
};

export default AdminServicePhotos;
